import * as React from 'react'
import { Command as CommandPrimitive } from 'cmdk'
import { Search } from 'lucide-react'
import { cn } from '@shared/utils'
import { Dialog, DialogContent, DialogTitle } from './dialog'
import { ScrollArea } from './scroll-area'

/** Danh sách lệnh / lựa chọn có ô tìm (shadcn Command, rút gọn) — nền của Combobox */
export const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      'flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground',
      className,
    )}
    {...props}
  />
))
Command.displayName = 'Command'

export const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  // wrapperClassName: hộp lệnh dùng ô tìm có khung riêng (viền + nền nhạt) thay cho vạch dưới
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & { wrapperClassName?: string }
>(({ className, wrapperClassName, ...props }, ref) => (
  <div className={cn('flex items-center gap-2 border-b border-border px-3', wrapperClassName)}>
    <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        'h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground',
        className,
      )}
      {...props}
    />
  </div>
))
CommandInput.displayName = 'CommandInput'

export const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List> & { viewportClassName?: string }
>(({ className, viewportClassName, ...props }, ref) => (
  // Thanh cuộn mảnh dùng chung (ScrollArea) thay thanh cuộn của trình duyệt; giới hạn cao đặt ở Viewport.
  // cmdk tự cuộn mục đang chọn vào tầm nhìn bằng scrollIntoView — chạy được với Viewport vì đó là khung cuộn gần nhất.
  // `[&>div]:!block`: Radix bọc nội dung Viewport bằng div `display: table` → dòng dài nới rộng theo chữ, `truncate` mất tác dụng.
  <ScrollArea viewportClassName={cn('max-h-72 [&>div]:!block', viewportClassName)}>
    <CommandPrimitive.List ref={ref} className={cn('p-1', className)} {...props} />
  </ScrollArea>
))
CommandList.displayName = 'CommandList'

export const CommandEmpty = (
  props: React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>,
) => (
  <CommandPrimitive.Empty className="py-6 text-center text-sm text-muted-foreground" {...props} />
)

/** Nhóm lựa chọn có tiêu đề (shadcn): tiêu đề 10px chữ hoa nhạt như nhãn nhóm menu */
export const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      'overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.06em] [&_[cmdk-group-heading]]:text-muted-foreground',
      className,
    )}
    {...props}
  />
))
CommandGroup.displayName = 'CommandGroup'

export const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator ref={ref} className={cn('-mx-1 h-px bg-border', className)} {...props} />
))
CommandSeparator.displayName = 'CommandSeparator'

/** Phím tắt / chữ phụ canh phải trong một mục */
export const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn('ml-auto shrink-0 text-xs tracking-wide text-muted-foreground', className)} {...props} />
)

/**
 * Hộp lệnh toàn màn hình (shadcn `CommandDialog`): Dialog + Command. `title` cho trình đọc màn hình (ẩn).
 * `commandProps` chuyển xuống Command – VD `shouldFilter={false}` khi kết quả lọc ở server.
 */
export function CommandDialog({
  children,
  title = 'Tìm',
  commandProps,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string
  commandProps?: React.ComponentPropsWithoutRef<typeof CommandPrimitive>
}) {
  return (
    <Dialog {...props}>
      <DialogContent hideClose className="top-[12%] max-w-xl translate-y-0 gap-0 overflow-hidden rounded-xl p-0 shadow-xl sm:rounded-xl">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <Command {...commandProps} className={cn('rounded-lg', commandProps?.className)}>
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  )
}

export const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-default select-none items-start gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50',
      className,
    )}
    {...props}
  />
))
CommandItem.displayName = 'CommandItem'
